import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { GamesService } from '../services/games.service';

import { Injectable } from '@angular/core';
import { AuthService } from '@core/services';
import { Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { NetworkErrorDialogComponent } from '@shared/components/dialogs/network-error-dialog/network-error-dialog.component';

/**
 * Дожидается инициализации GAMES и GAME_GROUPS в games.service.ts.
 */
@Injectable({
  providedIn: 'root',
})
export class GameTypeAvailableGuard {
  constructor(
    private readonly _authService: AuthService,
    private readonly _gamesService: GamesService,
    private readonly _router: Router,
    private readonly _dialog: MatDialog,
  ) {}

  provideAccess(gameType: string): boolean {
    const isAvailable = this._gamesService.isGameAvailable(gameType);
    if (!isAvailable) {
      this._router.navigate(['/game-not-found'], {
        queryParams: {
          g: gameType,
        },
      });
    }
    return isAvailable;
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    this._authService.checkAuth();
    const gameType = next.paramMap.get('gameType');
    if (!this._authService.isLogged) {
      this._router.navigate(['/']);
      return false;
    }

    if (this._gamesService.availableGames.value) {
      return this.provideAccess(gameType);
    }

    return this._gamesService.availableGames.pipe(
      filter((value) => !!value),
      // Пустой массив — сигнал ошибочной загрузки (см. GamesService.updateAvailableGames):
      // provideAccess для отсутствующей игры вернёт false — fail-closed.
      // При сетевой ошибке (флаг availableGamesLoadError) дополнительно показываем
      // диалог «Ошибка сети» с кнопкой «Повторить» (повторная навигация).
      map((res) => {
        if (this._gamesService.availableGamesLoadError.value && !res?.length) {
          this._dialog.open(NetworkErrorDialogComponent, {
            width: '90vw',
            maxWidth: '600px',
            hasBackdrop: true,
            panelClass: 'subscription-empty-dialog',
            backdropClass: 'blur-backdrop',
            data: {
              onRetry: () => {
                this._gamesService.updateAvailableGames();
                this._router.navigateByUrl(state.url);
              },
            },
          });
        }
        return this.provideAccess(gameType);
      }),
      first(),
    );
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canActivate(next, state);
  }
}
